import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCustomers } from "../../../../../api/customers";
import { getDisplayAccountType } from "../../../../../utils/accountTypeUtils";
import { buildPlatformCustomerScopeParams } from "../../utils/platformScope";

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatQuickbooksDate = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return "unknown date";

  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${month}/${day}/${year}`;
};

const createStatusMeta = ({
  label,
  tone = "muted",
  tooltip = "",
  interactive = false,
} = {}) => ({
  label,
  tone,
  tooltip,
  interactive,
});

const deriveQuickbooksSyncStatus = (syncStatuses) => {
  if (!syncStatuses) {
    return createStatusMeta({
      label: "Not Synced",
      tone: "muted",
      tooltip: "Sync status unknown.",
      interactive: false,
    });
  }

  const hasSyncData = Boolean(
    syncStatuses.gl_last_sync_success ||
      syncStatuses.gl_last_sync_attempt ||
      syncStatuses.tb_last_sync_success ||
      syncStatuses.tb_last_sync_attempt ||
      syncStatuses.last_validation_attempt
  );

  if (!hasSyncData) {
    return createStatusMeta({
      label: "Not Synced",
      tone: "muted",
      tooltip: "Sync status unknown.",
      interactive: false,
    });
  }

  const glEndDate = parseDateValue(syncStatuses.gl_end_date);
  const glLastSyncSuccess = parseDateValue(syncStatuses.gl_last_sync_success);
  const glLastSyncAttempt = parseDateValue(syncStatuses.gl_last_sync_attempt);
  const validationAttempted = Boolean(syncStatuses.last_validation_attempt);
  const latestPassed = Boolean(syncStatuses.is_synced);

  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const lastDayOfLastMonth = new Date(
    lastMonth.getFullYear(),
    lastMonth.getMonth() + 1,
    0
  );

  const syncFailed =
    glLastSyncAttempt &&
    (!glLastSyncSuccess || glLastSyncAttempt > glLastSyncSuccess);

  if (syncFailed) {
    return createStatusMeta({
      label: "Sync Issues",
      tone: "danger",
      tooltip: "Latest sync attempt failed. Click to view validation report.",
      interactive: true,
    });
  }

  if (glLastSyncSuccess && glEndDate && glEndDate >= lastDayOfLastMonth) {
    if (latestPassed && validationAttempted) {
      const daysSinceSync = Math.floor(
        (today - glLastSyncSuccess) / (1000 * 60 * 60 * 24)
      );
      return createStatusMeta({
        label: daysSinceSync <= 7 ? "Healthy" : "Recent",
        tone: daysSinceSync <= 7 ? "success" : "warning",
        tooltip: `Last synced ${daysSinceSync} days ago. Click to view validation report.`,
        interactive: true,
      });
    }

    if (latestPassed) {
      return createStatusMeta({
        label: "Sync Needed",
        tone: "warning",
        tooltip: "Data is current but validation missing. Please review.",
        interactive: true,
      });
    }

    if (validationAttempted) {
      return createStatusMeta({
        label: "Validation Pending",
        tone: "warning",
        tooltip: "Validation initiated but did not complete.",
        interactive: true,
      });
    }
  }

  const isCurrentData = Boolean(
    glEndDate &&
      glLastSyncSuccess &&
      glEndDate >= lastDayOfLastMonth &&
      glLastSyncSuccess >= lastDayOfLastMonth &&
      latestPassed
  );

  if (isCurrentData) {
    return createStatusMeta({
      label: "Sync Needed",
      tone: "warning",
      tooltip: "Sync data is current but validation pending.",
      interactive: true,
    });
  }

  if (!isCurrentData && glLastSyncSuccess) {
    const daysSinceSync = Math.floor(
      (today - glLastSyncSuccess) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceSync <= 30) {
      const tone = daysSinceSync >= 25 ? "warning" : "success";
      return createStatusMeta({
        label: "Sync Needed",
        tone,
        tooltip: `Data is not current. Last synced to ${
          formatQuickbooksDate(glEndDate)
        }.${tone === "warning" ? " Approaching 30-day limit." : ""}`,
        interactive: true,
      });
    }
  }

  return createStatusMeta({
    label: "Not Synced",
    tone: "muted",
    tooltip: "Sync status unknown.",
    interactive: false,
  });
};

export default function useQuickBooksTabData({ profile, rowsPerPage, sortConfig }) {
  const [realData, setRealData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [page, setPage] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const fetchRealDataRef = useRef();

  const transformCustomerToQuickBooksRow = useCallback((customer) => {
    const getQBStatus = (targetCustomer) => {
      const accountType = getDisplayAccountType(targetCustomer);

      if (accountType === "Trial" || accountType === "Demo") {
        return "Not Required";
      }

      return targetCustomer.quickbooks_connected_for_user
        ? "Connected"
        : "Disconnected";
    };

    const getNotes = (targetCustomer) => {
      const noteData = targetCustomer?.note;
      if (!noteData) {
        return "";
      }
      if (typeof noteData === "string") {
        return noteData;
      }
      if (typeof noteData === "object") {
        if (typeof noteData.note === "string") {
          return noteData.note;
        }
        if (typeof noteData.notes === "string") {
          return noteData.notes;
        }
        if (typeof noteData.text === "string") {
          return noteData.text;
        }
      }
      return "";
    };

    const hasNote = (targetCustomer) => {
      const noteText = getNotes(targetCustomer);
      return noteText && noteText.trim().length > 0;
    };

    const syncStatusMeta = deriveQuickbooksSyncStatus(
      customer.sync_statuses
    );

    return {
      id: customer.id,
      accountName: customer.name || "",
      primaryContact: customer.primary_contact || "",
      emailAddress: customer.email || "",
      quickbooksStatus: getQBStatus(customer),
      syncStatus: syncStatusMeta,
      notes: getNotes(customer),
      hasNote: hasNote(customer),
      originalCustomer: customer,
      isQBConnected: customer.quickbooks_connected_for_user || false,
      customerId: customer.id,
    };
  }, []);

  const fetchRealData = useCallback(
    async (cursor = null, search = "", isNewSearch = false, filters = {}) => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          page_size: rowsPerPage,
          include_profiles: false,
          include_quickbooks_connection: true,
          include_quickbooks_sync_info: true,
          include_notes: true,
        };

        if (cursor) {
          params.cursor = cursor;
        }

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (filters.accountName && filters.accountName.trim()) {
          params.name = filters.accountName.trim();
        }
        if (filters.primaryContact && filters.primaryContact.trim()) {
          params.primary_contact = filters.primaryContact.trim();
        }
        if (filters.emailAddress && filters.emailAddress.trim()) {
          params.email = filters.emailAddress.trim();
        }
        if (filters.quickbooksStatus && filters.quickbooksStatus.trim()) {
          params.quickbooks_status = filters.quickbooksStatus.trim();
        }
        if (filters.syncRange && filters.syncRange.trim()) {
          params.sync_range = filters.syncRange.trim();
        }
        if (filters.syncRangeStart && filters.syncRangeEnd) {
          params.sync_range_start = filters.syncRangeStart;
          params.sync_range_end = filters.syncRangeEnd;
        }

        if (sortConfig && sortConfig.field && sortConfig.direction) {
          const fieldMap = {
            accountName: "name",
            primaryContact: "primary_contact",
            emailAddress: "email",
          };

          const backendField = fieldMap[sortConfig.field] || sortConfig.field;
          const prefix = sortConfig.direction === "desc" ? "-" : "";
          params.ordering = `${prefix}${backendField}`;
        }

        const response = await fetchCustomers(
          profile,
          buildPlatformCustomerScopeParams(profile, params)
        );
        let transformedData = (response.results || []).map(
          transformCustomerToQuickBooksRow
        );

        if (filters.syncStatus && filters.syncStatus.trim()) {
          const target = filters.syncStatus.trim().toLowerCase();
          transformedData = transformedData.filter((row) => {
            const label =
              (row.syncStatus && row.syncStatus.label) || row.syncStatus || "";
            return label.toLowerCase() === target;
          });
        }

        setRealData(transformedData);
        setNextCursor(
          response.next ? new URL(response.next).searchParams.get("cursor") : null
        );
        setPrevCursor(
          response.previous
            ? new URL(response.previous).searchParams.get("cursor")
            : null
        );
      } catch (err) {
        setError("Failed to load QuickBooks connections data");
      } finally {
        setLoading(false);
      }
    },
    [profile, rowsPerPage, sortConfig, transformCustomerToQuickBooksRow]
  );

  fetchRealDataRef.current = fetchRealData;

  const handleNextPage = useCallback(() => {
    if (nextCursor) {
      setPage((prev) => prev + 1);
      fetchRealDataRef.current(nextCursor, searchTerm, false, activeFilters);
    }
  }, [activeFilters, nextCursor, searchTerm]);

  const handlePreviousPage = useCallback(() => {
    if (prevCursor) {
      setPage((prev) => prev - 1);
      fetchRealDataRef.current(prevCursor, searchTerm, false, activeFilters);
    } else {
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, false, activeFilters);
    }
  }, [activeFilters, searchTerm, prevCursor]);

  const handleSearchChange = useCallback(
    (searchValue) => {
      if (searchValue === searchTerm) {
        if (!searchValue || !searchValue.trim()) {
          setPage(0);
          fetchRealDataRef.current?.(null, "", true, activeFilters);
        }
        return;
      }
      if (!fetchRealDataRef.current) {
        return;
      }
      setSearchTerm(searchValue);
      setPage(0);
    },
    [searchTerm, activeFilters]
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      setActiveFilters(newFilters);
      setPage(0);
      fetchRealDataRef.current(null, searchTerm, true, newFilters);
    },
    [searchTerm]
  );

  const refresh = useCallback(() => {
    fetchRealDataRef.current(null, searchTerm, true, activeFilters);
  }, [searchTerm, activeFilters]);

  useEffect(() => {
    if (!profile) {
      setLoading(true);
      return;
    }

    fetchRealDataRef.current(null, "", true, {});
  }, [profile]);

  useEffect(() => {
    if (!fetchRealDataRef.current) {
      return;
    }
    setPage(0);
    fetchRealDataRef.current(null, searchTerm, true, activeFilters);
  }, [rowsPerPage, sortConfig, searchTerm, activeFilters]);

  return {
    data: realData,
    loading,
    error,
    searchTerm,
    page,
    nextCursor,
    prevCursor,
    handleNextPage,
    handlePreviousPage,
    handleSearchChange,
    handleFiltersChange,
    refresh,
  };
}
